import axios from "axios"

import { performRequest } from "../performRequest"

const withCompany = (params) => {
    const company = localStorage.getItem('company') || 1
    return { ...params, company }
}

// ==================== SIZES ====================
export const getUniformSizesApi = (params) => {
    return performRequest("get", "api/uniform/sizes/", params)
}

export const getUniformSizeDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/sizes/${id}/`, params)
}

export const createUniformSizeApi = (params) => {
    return performRequest("post", "api/uniform/sizes/", withCompany(params))
}

export const updateUniformSizeApi = (id, params) => {
    return performRequest("put", `api/uniform/sizes/${id}/`, withCompany(params))
}

export const deleteUniformSizeApi = (id) => {
    return performRequest("delete", `api/uniform/sizes/${id}/`)
}

// ==================== ITEMS ====================
export const getUniformItemsApi = (params) => {
    return performRequest("get", "api/uniform/items/", params)
}

export const getUniformItemDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/items/${id}/`, params)
}

export const createUniformItemApi = (params) => {
    return performRequest("post", "api/uniform/items/", withCompany(params))
}

export const updateUniformItemApi = (id, params) => {
    return performRequest("put", `api/uniform/items/${id}/`, withCompany(params))
}

export const deleteUniformItemApi = (id) => {
    return performRequest("delete", `api/uniform/items/${id}/`)
}

// ==================== POSITION MAPPINGS ====================
export const getPositionMappingsApi = (params) => {
    return performRequest("get", "api/uniform/positions/", params)
}

export const getPositionMappingDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/positions/${id}/`, params)
}

export const createPositionMappingApi = (params) => {
    return performRequest("post", "api/uniform/positions/", withCompany(params))
}

export const updatePositionMappingApi = (id, params) => {
    return performRequest("put", `api/uniform/positions/${id}/`, withCompany(params))
}

export const deletePositionMappingApi = (id) => {
    return performRequest("delete", `api/uniform/positions/${id}/`)
}

/** Writes the full cross-product of `uniform_items` x `positions` in one
 *  atomic, company-scoped request, upserting rather than erroring on a pair
 *  that already exists.
 *
 *  @param {object} params
 *    - uniform_items {number[]} item ids
 *    - positions {number[]} position ids
 *    - rotation_cycle_months_override {number|null} OPTIONAL, and the presence
 *      of the key is itself meaningful. OMIT it to leave existing rows'
 *      overrides untouched (new rows get null, and untouched rows come back
 *      counted as `unchanged`). SEND it — including an explicit null, which
 *      means "clear the override" — to apply that value to every pair in the
 *      cross-product, new and existing alike. Callers must therefore only
 *      include the key when the user has actively set or cleared it; sending
 *      it as an incidental default silently overwrites hand-tuned overrides.
 *  @returns response.data.data = { created, updated, unchanged, total }, where
 *    created + updated + unchanged === total. Callers must report those counts
 *    rather than collapsing them to a generic success — re-running an
 *    assignment is safe precisely because the user can see what changed. */
export const bulkUpsertPositionMappingsApi = (params) => {
    return performRequest("post", "api/uniform/positions/bulk/", withCompany(params))
}

// ==================== WAREHOUSES ====================
export const getWarehousesApi = (params) => {
    return performRequest("get", "api/uniform/warehouses/", params)
}

export const getWarehouseDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/warehouses/${id}/`, params)
}

export const createWarehouseApi = (params) => {
    return performRequest("post", "api/uniform/warehouses/", withCompany(params))
}

export const updateWarehouseApi = (id, params) => {
    return performRequest("put", `api/uniform/warehouses/${id}/`, withCompany(params))
}

export const deleteWarehouseApi = (id) => {
    return performRequest("delete", `api/uniform/warehouses/${id}/`)
}

// ==================== WAREHOUSE STOCK ====================
export const getWarehouseStockApi = (warehouseId, params) => {
    return performRequest("get", `api/uniform/warehouses/${warehouseId}/stock/`, params)
}

// ==================== STOCK RECEIVING ====================
/** @deprecated use purchase orders (createPurchaseOrderApi + receivePurchaseOrderApi)
 *  instead — the ad-hoc receiving screen was retired in favour of PO-based receiving.
 *  Kept for one release in case anything still references it directly. */
export const receiveStockApi = (params) => {
    return performRequest("post", "api/uniform/stock/receive/", withCompany(params))
}

// ==================== LOW STOCK ====================
export const getLowStockApi = (params) => {
    return performRequest("get", "api/uniform/stock/low-stock/", params)
}

// ==================== STOCK TRANSACTIONS ====================
export const getStockTransactionsApi = (params) => {
    return performRequest("get", "api/uniform/transactions/", params)
}

// ==================== EMPLOYEE UNIFORM PROFILE ====================
export const getMyUniformProfileApi = (params) => {
    return performRequest("get", "api/uniform/profile/", params)
}

export const getEmployeeUniformProfileApi = (userId, params) => {
    return performRequest("get", `api/uniform/profile/${userId}/`, params)
}

// ==================== PENDING SIZE SELECTION ====================
export const getPendingSizeSelectionApi = (params) => {
    return performRequest("get", "api/uniform/profile/pending/", params)
}

// ==================== SIZE SELECTION ====================
export const submitSizeSelectionApi = (params) => {
    return performRequest("post", "api/uniform/profile/select/", withCompany(params))
}

// ==================== SIZE CHANGE ====================
export const changeSizeApi = (params) => {
    return performRequest("put", "api/uniform/profile/change-size/", withCompany(params))
}

// ==================== PRE-ROTATION SIZE CONFIRMATION ====================
// The employee's preferred size per uniform, with the window in which it can
// still be changed. Pass { pending: 1 } for the cheap app-load call: only the
// uniforms still waiting on an answer, so an empty list means nothing to ask.
export const getSizeConfirmationApi = (params) => {
    return performRequest("get", "api/uniform/profile/size-confirmation/", params)
}

export const submitSizeConfirmationApi = (params) => {
    return performRequest("post", "api/uniform/profile/size-confirmation/", withCompany(params))
}

// ==================== SIZE CHANGE HISTORY ====================
export const getSizeChangeHistoryApi = (userId, params) => {
    return performRequest("get", `api/uniform/profile/${userId}/history/`, params)
}

// ==================== ISSUANCE ====================
export const getIssuancesApi = (params) => {
    return performRequest("get", "api/uniform/issuance/", params)
}

export const getIssuanceDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/issuance/${id}/`, params)
}

export const createIssuanceApi = (params) => {
    return performRequest("post", "api/uniform/issuance/", withCompany(params))
}

// Employees eligible for issuance (CASA/gender/position/search scoped), with their
// position-mapped items and profile sizes — drives the CASA-first single issuance modal.
export const getIssuanceEmployeesApi = (params) => {
    return performRequest("get", "api/uniform/issuance/employees/", params)
}

// ==================== ISSUANCE BATCHES ====================
export const getIssuanceBatchesApi = (params) => {
    return performRequest("get", "api/uniform/issuance/batches/", params)
}

export const getIssuanceBatchApi = (id, params) => {
    return performRequest("get", `api/uniform/issuance/batches/${id}/`, params)
}

// ==================== BULK ISSUANCE ====================
export const getBulkIssuanceMetaApi = (params) => {
    return performRequest("get", "api/uniform/issuance/bulk/meta/", params)
}

export const createBulkIssuanceApi = (params) => {
    return performRequest("post", "api/uniform/issuance/bulk/", withCompany(params))
}

// ==================== DUE REPLACEMENTS ====================
export const getDueReplacementsApi = (params) => {
    return performRequest("get", "api/uniform/issuance/due-replacements/", params)
}

export const exportDueReplacementsApi = (params) => {
    return performRequest("get", "api/uniform/issuance/due-replacements/", { ...params, export: "xlsx" }, true, "blob")
}

// ==================== MY PENDING ISSUANCES (for accept/reject popup) ====================
export const getMyPendingIssuancesApi = (params) => {
    return performRequest("get", "api/uniform/issuance/my-pending/", params)
}

// ==================== ISSUANCE ACCEPT / REJECT ====================
export const acceptIssuanceApi = (id) => {
    return performRequest("post", `api/uniform/issuance/${id}/accept/`)
}

export const rejectIssuanceApi = (id, params) => {
    return performRequest("post", `api/uniform/issuance/${id}/reject/`, withCompany(params))
}

// ==================== REJECTION REVIEW ====================
export const reviewRejectionApi = (id, params) => {
    return performRequest("post", `api/uniform/rejection/${id}/review/`, withCompany(params))
}

// ==================== STOCK TRANSFERS ====================
export const getStockTransfersApi = (params) => {
    return performRequest("get", "api/uniform/stock/transfer/", params)
}

export const createStockTransferApi = (params) => {
    return performRequest("post", "api/uniform/stock/transfer/", withCompany(params))
}

export const completeStockTransferApi = (id) => {
    return performRequest("post", `api/uniform/stock/transfer/${id}/complete/`)
}

export const cancelStockTransferApi = (id) => {
    return performRequest("post", `api/uniform/stock/transfer/${id}/cancel/`)
}

export const getStockTransferDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/stock/transfer/${id}/`, params)
}

export const getStockTransferDocumentApi = (id, params) => {
    return performRequest("get", `api/uniform/stock/transfer/${id}/document/`, params)
}

// ==================== PURCHASE ORDERS ====================
export const getPurchaseOrdersApi = (params) => {
    return performRequest("get", "api/uniform/purchase-orders/", params)
}

export const getPurchaseOrderApi = (id, params) => {
    return performRequest("get", `api/uniform/purchase-orders/${id}/`, params)
}

export const createPurchaseOrderApi = (params) => {
    return performRequest("post", "api/uniform/purchase-orders/", withCompany(params))
}

export const updatePurchaseOrderApi = (id, params) => {
    return performRequest("patch", `api/uniform/purchase-orders/${id}/`, withCompany(params))
}

export const confirmPurchaseOrderApi = (id) => {
    return performRequest("post", `api/uniform/purchase-orders/${id}/confirm/`, withCompany({}))
}

export const cancelPurchaseOrderApi = (id) => {
    return performRequest("post", `api/uniform/purchase-orders/${id}/cancel/`, withCompany({}))
}

export const receivePurchaseOrderApi = (id, params) => {
    return performRequest("post", `api/uniform/purchase-orders/${id}/receive/`, withCompany(params))
}

export const getPurchaseOrderDocumentApi = (id, kind, params) => {
    return performRequest("get", `api/uniform/purchase-orders/${id}/documents/${kind}/`, params)
}

// ==================== STOCK RECEIPTS ====================
export const getStockReceiptsApi = (params) => {
    return performRequest("get", "api/uniform/receipts/", params)
}

// ==================== PRIVATE DOCUMENT UPLOAD ====================
export const getUniformUploadUrlApi = (params) => {
    return performRequest("get", "api/uniform/documents/upload-url/", params)
}

export const ALLOWED_DOCUMENT_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"]

/** Upload a PO/invoice file to the private uniform document store.
 *  Resolves to { document_url, document_name }. Throws Error with a user message. */
export const uploadUniformDocument = async (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase()
    if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(ext)) throw new Error("Only PDF, JPG, JPEG or PNG files are allowed.")
    const { data } = await getUniformUploadUrlApi({ file_name: file.name })
    const { url, fields, document_url, max_bytes } = data.data
    if (file.size > max_bytes) throw new Error(`File is larger than ${Math.round(max_bytes / 1024 / 1024)} MB.`)
    const formData = new FormData()
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
    formData.append("file", file)
    await axios.post(url, formData)
    return { document_url, document_name: file.name }
}

/** Start a download for `url` via a throwaway anchor targeting a new context.
 *
 *  Used only for `attachment` URLs. `target="_blank"` is load-bearing and is
 *  what makes this safe in both directions:
 *
 *  - When the response carries `Content-Disposition: attachment` — what the
 *    backend stamps onto these presigned URLs — the navigation is converted
 *    into a download and never commits, so the new browsing context is
 *    discarded. The file downloads and no tab is left behind. That is the
 *    stray-tab bug this replaced: `window.open("", "_blank")` opens and
 *    commits a real about:blank tab BEFORE the URL is known, so nothing can
 *    reclaim it afterwards.
 *  - If a document is ever served inline instead — a misconfiguration, or a
 *    content type the backend does not stamp — it opens in a new tab. Without
 *    the target it would replace the current document, unloading the SPA and
 *    destroying unsaved issuance or transfer state. A stray tab is a nuisance;
 *    losing a half-filled bulk issuance is not.
 *
 *  Not fetch-to-blob with a real `download` filename: that needs CORS on the
 *  S3 response, buffers the whole file in memory, and loses the browser's own
 *  download UI for what is already a correctly-stamped attachment.
 *
 *  `download` is not set either — it is ignored on cross-origin URLs like the
 *  presigned one, so it would imply a guarantee this cannot make. `rel` is set
 *  because there IS a target: it stops the opened context getting a handle on
 *  `window.opener`. */
const navigateToDownload = (url) => {
    const a = document.createElement("a")
    a.href = url
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    a.remove()
}

/** Open (inline) or download (attachment) a stored document via its short-lived signed URL.
 *
 *  `inline` opens a tab, and opens it synchronously (before the await) so popup
 *  blockers don't swallow it.
 *
 *  `attachment` deliberately does NOT pre-open a tab. The backend stamps
 *  `Content-Disposition: attachment` onto the presigned URL, so the browser
 *  downloads the file instead of rendering it — leaving the tab we opened
 *  sitting there blank and unclosable by us (once it has navigated to another
 *  origin, `tab.close()` from here is a no-op). It hands the URL to an anchor
 *  targeting a new context instead, which a download never commits — see
 *  `navigateToDownload` for why the target matters in the inline case. */
export const openUniformDocument = async (fetchFn, disposition = "inline") => {
    if (disposition === "attachment") {
        const { data } = await fetchFn({ disposition })
        navigateToDownload(data.data.url)
        return
    }
    const tab = window.open("", "_blank", "noopener")
    try {
        const { data } = await fetchFn({ disposition })
        if (tab) tab.location.href = data.data.url
        else window.open(data.data.url, "_blank", "noopener")
    } catch (err) {
        if (tab) tab.close()
        throw err
    }
}

/** Extract a user-facing message from an API error response, falling back to
 *  the error's own message (e.g. client-side validation errors thrown as plain Errors). */
// Container keys that carry no meaning for a reader, so they are not used as a
// prefix. Everything else (a real field name like `po_number`) is.
const ERROR_WRAPPER_KEYS = new Set([
    "status", "error", "errors", "detail", "non_field_errors", "field_errors", "lines",
])

/** Flatten any DRF error payload into readable sentences.
 *
 *  DRF nests arbitrarily deep — a serializer inside a serializer produces shapes
 *  like {"error": {"lines": {"lines": ["Line 6: duplicate item/size line."]}}} —
 *  so anything that stringifies a branch with String() renders "[object Object]"
 *  at some depth. Recursing is the only shape-independent way to read these.
 *  Exported because callers also need it to build per-field setError messages. */
export const flattenErrorMessages = (value, key = null) => {
    if (value === null || value === undefined) return []
    if (typeof value === "string") {
        const trimmed = value.trim()
        if (!trimmed) return []
        return [key && !ERROR_WRAPPER_KEYS.has(key) ? `${key}: ${trimmed}` : trimmed]
    }
    if (Array.isArray(value)) return value.flatMap((entry) => flattenErrorMessages(entry, key))
    if (typeof value === "object") {
        return Object.entries(value)
            .filter(([k]) => k !== "status")
            .flatMap(([k, entry]) => flattenErrorMessages(entry, k))
    }
    return [String(value)]
}

export const uniformErrorMessage = (err, fallback = "Something went wrong") => {
    const data = err?.response?.data
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
        const source = data.error ?? data.detail ?? data.non_field_errors ?? data
        const messages = flattenErrorMessages(source)
        if (messages.length) {
            // Cap the toast: a bulk request can fail on dozens of rows at once.
            const shown = messages.slice(0, 5).join(" | ")
            const remaining = messages.length - 5
            return remaining > 0 ? `${shown} (and ${remaining} more)` : shown
        }
    }
    return err?.message || fallback
}

// ==================== STOCK TAKE ====================
export const getStockTakesApi = (params) => {
    return performRequest("get", "api/uniform/stock-take/", params)
}

export const getStockTakeDetailApi = (id, params) => {
    return performRequest("get", `api/uniform/stock-take/${id}/`, params)
}

export const createStockTakeApi = (params) => {
    return performRequest("post", "api/uniform/stock-take/", withCompany(params))
}

export const updateStockTakeApi = (id, params) => {
    return performRequest("put", `api/uniform/stock-take/${id}/`, withCompany(params))
}

export const completeStockTakeApi = (id) => {
    return performRequest("post", `api/uniform/stock-take/${id}/complete/`)
}

// ==================== EMPLOYEE UNIFORM CARD ====================
export const getEmployeeUniformCardApi = (userId, params) => {
    return performRequest("get", `api/uniform/employee-card/${userId}/`, params)
}

// ==================== REPORTS ====================
export const getInventoryReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/inventory/", params)
}

// CASA-wise issuance report (server-paginated). Reads the CASA/position/size
// recorded ON THE ISSUANCE at issue time, not the employee's current values.
export const getIssuanceReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/issuance/", params)
}

export const exportIssuanceReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/issuance/", { ...params, export: "xlsx" }, true, "blob")
}

// Same row shape/filters as the issuance report, scoped to one employee.
// Now paginated ({count, next, previous, results}) — no longer {status, data}.
export const getEmployeeHistoryReportApi = (userId, params) => {
    return performRequest("get", `api/uniform/reports/employee-history/${userId}/`, params)
}

export const exportEmployeeHistoryReportApi = (userId, params) => {
    return performRequest("get", `api/uniform/reports/employee-history/${userId}/`, { ...params, export: "xlsx" }, true, "blob")
}

export const getTransferReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/transfers/", params)
}

export const exportTransferReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/transfers/", { ...params, export: "xlsx" }, true, "blob")
}

export const getPurchaseOrderReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/purchase-orders/", params)
}

export const exportPurchaseOrderReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/purchase-orders/", { ...params, export: "xlsx" }, true, "blob")
}

// Employees whose size profile is incomplete for a uniform their position
// wears (A-15). Same filters as the issuance report minus dates; grouped
// grains (?group_by=cost_center|employee) are unpaginated, the detail is
// paginated. Uniforms that come in one size or none are never rows here.
export const getIncompleteSizeProfilesReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/incomplete-size-profiles/", params)
}

export const exportIncompleteSizeProfilesReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/incomplete-size-profiles/", { ...params, export: "xlsx" }, true, "blob")
}

export const getLifecycleReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/lifecycle/", params)
}

export const getVarianceReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/variance/", params)
}

export const exportInventoryReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/inventory/", { ...params, export: "xlsx" }, true, "blob")
}

export const exportVarianceReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/variance/", { ...params, export: "xlsx" }, true, "blob")
}

export const exportLifecycleReportApi = (params) => {
    return performRequest("get", "api/uniform/reports/lifecycle/", { ...params, export: "xlsx" }, true, "blob")
}
